import { CalendarDays, Check, MapPin, Navigation, PackageCheck, Store } from 'lucide-react'
import { Link } from 'react-router-dom'
import heroImage from '@/assets/homepage-delivery-hero.webp'
import stylereLogo from '@/assets/stylere-logo.png'
import { ROUTES } from '@/lib/navigation'

export default function PublicHero() {
  return (
    <section className="public-hero" aria-labelledby="public-home-title">
      <div className="public-hero__copy">
        <p className="public-eyebrow">
          <span aria-hidden="true" />
          Same-day. Local. Reliable.
        </p>
        <h1 id="public-home-title" className="public-hero__title">
          Fashion delivery,
          <br />
          from <em>store</em> to <em>door.</em>
        </h1>
        <p className="public-hero__description">
          We collect ready-for-pickup fashion orders from supported local stores and bring them to
          your door, with same-day options where available.
        </p>

        <div className="public-hero__actions">
          <Link
            to={ROUTES.STORE_DETAILS}
            className="public-button public-button--primary public-button--hero"
          >
            <CalendarDays aria-hidden="true" />
            Schedule a Pickup
          </Link>
          <Link
            to={ROUTES.PICKUP_HISTORY}
            className="public-button public-button--secondary public-button--hero"
          >
            <MapPin aria-hidden="true" />
            Track Your Order
          </Link>
        </div>

        <div className="public-hero__proof">
          <div className="public-hero__avatars" aria-hidden="true">
            {['AS', 'JM', 'TL', 'KR'].map((initials, index) => (
              <span key={initials} style={{ '--avatar-index': index } as React.CSSProperties}>
                {initials}
              </span>
            ))}
          </div>
          <p>Built for shoppers, stylists, and busy professionals.</p>
        </div>
      </div>

      <div className="public-hero__visual">
        <img
          src={heroImage}
          alt="A delivery professional collecting fashion parcels outside a modern store"
          className="public-hero__image"
        />
        <img src={stylereLogo} alt="" className="public-hero__shirt-logo" aria-hidden="true" />
        <div className="public-hero__image-shade" aria-hidden="true" />

        <article className="delivery-status-card" aria-label="Sample delivery progress">
          <div className="demo-label">Sample delivery</div>
          <div className="delivery-status-card__store">
            <span className="delivery-status-card__icon">
              <Store aria-hidden="true" />
            </span>
            <div>
              <small>Pickup from</small>
              <strong>Selected store</strong>
              <span>Ready order confirmed</span>
            </div>
          </div>
          <ol className="delivery-status-card__timeline">
            <li className="is-complete">
              <span>
                <Check aria-hidden="true" />
              </span>
              <div>
                <strong>Ready for pickup</strong>
                <small>Store confirmed</small>
              </div>
            </li>
            <li className="is-current">
              <span>
                <Navigation aria-hidden="true" />
              </span>
              <div>
                <strong>Out for delivery</strong>
                <small>Updates in real time</small>
              </div>
            </li>
            <li>
              <span>
                <PackageCheck aria-hidden="true" />
              </span>
              <div>
                <strong>Delivered</strong>
                <small>Confirmation follows</small>
              </div>
            </li>
          </ol>
        </article>

        <article className="tracking-card" aria-label="Sample live tracking card">
          <div className="tracking-card__heading">
            <div>
              <strong>Live Tracking</strong>
              <span>ETA updates after pickup</span>
            </div>
            <span className="demo-label">Demo</span>
          </div>
          <div className="tracking-card__map" aria-hidden="true">
            <span className="tracking-card__street tracking-card__street--one" />
            <span className="tracking-card__street tracking-card__street--two" />
            <span className="tracking-card__street tracking-card__street--three" />
            <span className="tracking-card__park" />
            <span className="tracking-card__route" />
            <span className="tracking-card__driver">
              <Navigation />
            </span>
            <span className="tracking-card__destination">
              <MapPin />
            </span>
          </div>
          <div className="tracking-card__driver-row">
            <span className="tracking-card__driver-avatar">
              <Navigation aria-hidden="true" />
            </span>
            <div>
              <small>Your delivery partner</small>
              <strong>Assigned after pickup</strong>
            </div>
          </div>
        </article>
      </div>
    </section>
  )
}
